import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { ProductionDeclarationMaterialCommentDeleteDialog } from '@/features/production-declaration/components/statement/production-declaration-material-comment-delete-dialog';
import { ProductionDeclarationMaterialCommentEditDialog } from '@/features/production-declaration/components/statement/production-declaration-material-comment-edit-dialog';
import { ProductionDeclarationMaterialCommentForm } from '@/features/production-declaration/components/statement/production-declaration-material-comment-form';
import { ProductionDeclarationMaterialCorrectionRequestsList } from '@/features/production-declaration/components/statement/production-declaration-material-correction-requests-list';
import type { useProductionDeclarationCorrectionRequestComment } from '@/features/production-declaration/hooks/use-production-declaration-correction-request-comment';

type CommentState = ReturnType<
  typeof useProductionDeclarationCorrectionRequestComment
>;

type ProductionDeclarationMaterialCommentsSectionProps = {
  canComment?: boolean;
  comments: CommentState['comments'];
  create: CommentState['create'];
  edit: CommentState['edit'];
  remove: CommentState['remove'];
  resolution: CommentState['resolution'];
  showSupplierCorrectionComments?: boolean;
};

export function ProductionDeclarationMaterialCommentsSection({
  canComment = false,
  comments,
  create,
  edit,
  remove,
  resolution,
  showSupplierCorrectionComments = false,
}: ProductionDeclarationMaterialCommentsSectionProps) {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const audience = isAuthenticated ? 'internal' : 'supplier';
  const visibleComments =
    audience === 'supplier' && !showSupplierCorrectionComments ? [] : comments;
  const hasUnresolvedComments = visibleComments.some(
    (comment) => !comment.isResolved,
  );

  return (
    <>
      {create.isOpen ? (
        <ProductionDeclarationMaterialCommentForm
          comment={create.comment}
          isPending={create.isPending}
          onCancel={create.cancel}
          onChange={create.setComment}
          onSubmit={create.submit}
        />
      ) : null}
      {audience === 'supplier' && hasUnresolvedComments ? (
        <p className='font-semibold text-red-600 text-sm leading-6'>
          {t('materialCommodityOrigins.comments.supplierCorrectionNotice')}
        </p>
      ) : null}
      <ProductionDeclarationMaterialCorrectionRequestsList
        audience={audience}
        canUpdateCommentResolution={canComment}
        comments={visibleComments}
        isUpdatingCommentResolution={resolution.isPending}
        onCommentResolutionChange={resolution.toggle}
        onDeleteClick={canComment ? remove.open : undefined}
        onEditClick={canComment ? edit.open : undefined}
      />
      <ProductionDeclarationMaterialCommentEditDialog
        comment={edit.comment}
        isPending={edit.isPending}
        onCancel={edit.cancel}
        onChange={edit.setComment}
        onOpenChange={edit.onOpenChange}
        onSubmit={edit.submit}
        open={edit.isOpen}
      />
      <ProductionDeclarationMaterialCommentDeleteDialog
        isPending={remove.isPending}
        onConfirm={remove.confirm}
        onOpenChange={remove.onOpenChange}
        open={remove.isOpen}
      />
    </>
  );
}
